/**
 * Consolidate invoice line items for display / PDF.
 *
 * Billed time and fixed fees that share a person + cost code are rolled into one
 * line (hours summed, blended rate = total / hours). Distinct people with the
 * same cost code stay separate so rates are never blended across people.
 * Items without a cost code, or from other source types, pass through unchanged.
 */

export type InvoiceLineItemLike = {
  type?: string;
  description?: string;
  item?: string;
  qty?: number;
  amount?: number;
  discount?: number;
  id?: string;
  sourceType?: string;
};

const GROUPABLE_SOURCE_TYPES = new Set(['billed time', 'fixed fee']);

export function isGroupableInvoiceLineItem(li: InvoiceLineItemLike): boolean {
  const code = (li.item || '').trim();
  return Boolean(code && GROUPABLE_SOURCE_TYPES.has(li.sourceType || ''));
}

export function consolidateInvoiceLineItems<T extends InvoiceLineItemLike>(
  items: T[]
): Array<T & InvoiceLineItemLike> {
  const grouped = new Map<
    string,
    {
      totalQty: number;
      totalAmount: number;
      item: string;
      type: string;
      sourceType: string;
      names: string[];
    }
  >();
  const groupOrder: string[] = [];
  const passThrough: T[] = [];

  items.forEach((li) => {
    if (!isGroupableInvoiceLineItem(li)) {
      passThrough.push(li);
      return;
    }
    const code = (li.item || '').trim();
    const namePart = String(li.description || '').trim();
    // Group per person + cost code so distinct people are never merged into one
    // blended-rate line. Combine only the same person's entries for a cost code.
    const key = `${li.sourceType}::${namePart}::${code}`;
    const existing = grouped.get(key);
    const qty = li.qty || 0;
    if (existing) {
      existing.totalQty += qty;
      existing.totalAmount += (li.amount || 0) * qty;
      if (namePart) existing.names.push(namePart);
    } else {
      grouped.set(key, {
        totalQty: qty,
        totalAmount: (li.amount || 0) * qty,
        item: code,
        type: li.type || 'money_in',
        sourceType: li.sourceType || '',
        names: namePart ? [namePart] : [],
      });
      groupOrder.push(key);
    }
  });

  const consolidated = groupOrder.map((key) => {
    const g = grouped.get(key)!;
    const uniqueNames = [...new Set(g.names.filter(Boolean))];
    return {
      type: g.type,
      description: uniqueNames.join(', '),
      item: g.item,
      qty: g.totalQty,
      amount: g.totalQty > 0 ? g.totalAmount / g.totalQty : 0,
      discount: 0,
      sourceType: g.sourceType,
    } as T & InvoiceLineItemLike;
  });

  return [...consolidated, ...passThrough];
}

/** Collect source-row ids from saved invoice line_items, keyed by sourceType. */
export function linkedSourceIdsByType(
  lineItems: Array<{ id?: unknown; sourceType?: unknown }> | null | undefined
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const li of lineItems || []) {
    const sourceType = String(li?.sourceType || '').trim();
    if (!sourceType || li?.id == null || li.id === '') continue;
    let set = map.get(sourceType);
    if (!set) {
      set = new Set<string>();
      map.set(sourceType, set);
    }
    set.add(String(li.id));
  }
  return map;
}

export function expenseRowId(item: {
  disbursementid?: unknown;
  ven_id?: unknown;
  opp_id?: unknown;
  gen_id?: unknown;
  ge_id?: unknown;
}): string {
  return String(
    item.disbursementid || item.ven_id || item.opp_id || item.gen_id || item.ge_id || ''
  );
}

export function isLinkedExpense(
  exp: {
    disbursementid?: unknown;
    ven_id?: unknown;
    opp_id?: unknown;
    gen_id?: unknown;
    ge_id?: unknown;
  },
  linkedIds: Map<string, Set<string>>
): boolean {
  const id = expenseRowId(exp);
  if (!id) return false;
  if (exp.ge_id != null || exp.gen_id != null) {
    return Boolean(
      linkedIds.get('gen expense')?.has(id) || linkedIds.get('general expense')?.has(id)
    );
  }
  if (exp.opp_id != null) return Boolean(linkedIds.get('opp expense')?.has(id));
  if (exp.ven_id != null) return Boolean(linkedIds.get('ven expense')?.has(id));
  return Boolean(linkedIds.get('disbursement')?.has(id));
}

export function dedupeByKey<T>(items: T[], keyFn: (item: T) => string): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of items) {
    const key = keyFn(item);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}
